import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import InfinityTimeline from '../components/InfinityTimeline';
import RiverOfLifeSection from '../components/RiverOfLifeSection';
import { ArrowRight, Menu, X, ChevronDown, Sparkles, Clock, Heart, Layers } from 'lucide-react';

const C = {
  green:   '#D4A574',
  bg:      '#0A0A0A',
  bgAlt:   '#141414',
  card:    '#141414',
  text:    '#FFFFFF',
  textSec: '#B8B8B8',
  textMut: '#666666',
  border:  '#2A2A2A',
};

const serif = "'Georgia', 'Times New Roman', serif";

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { if (user) navigate('/memories'); }, [user, navigate]);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: C.bg }}>
      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ background: 'rgba(10,10,10,0.88)', borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" data-testid="logo">
              {/* < 640px: mark only at md (32px) for solo presence */}
              <span className="inline-flex sm:hidden"><Logo size="md" markOnly /></span>
              {/* ≥ 640px: full lockup at md (32px) — mark stays constant size */}
              <span className="hidden sm:inline-flex"><Logo size="md" showTagline={false} /></span>
            </Link>
            <div className="hidden sm:flex items-center gap-5">
              <Link to="/pricing" className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: C.textSec }} data-testid="pricing-link">Pricing</Link>
              <Link to="/login" className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: C.textSec }} data-testid="sign-in-link">Sign In</Link>
              <Link to="/signup" className="px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-[1.03] hover:shadow-lg" style={{ background: C.green, color: '#0A0A0A' }} data-testid="get-started-btn">Get Started</Link>
            </div>
            <div className="flex sm:hidden items-center gap-2">
              <Link to="/signup" className="px-4 py-2 rounded-full text-xs font-medium" style={{ background: C.green, color: '#0A0A0A' }} data-testid="get-started-btn-mobile">Get Started</Link>
              <button onClick={() => setMenuOpen(!menuOpen)} className="w-10 h-10 flex items-center justify-center" style={{ color: C.textSec }} data-testid="mobile-menu-toggle">
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        {menuOpen && (
          <div className="sm:hidden" style={{ background: C.card, borderTop: `1px solid ${C.border}` }}>
            <div className="px-4 py-3 space-y-1">
              <Link to="/pricing" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium" style={{ color: C.textSec }}>Pricing</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium" style={{ color: C.textSec }}>Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      <SectionHero />
      <SectionScattered />
      <RiverOfLifeSection />
      <SectionCameraRoll />
      <SectionCoreShift />
      <SectionEmotional />
      <SectionIdentity />
      <SectionFuture />
      <SectionFinalCTA />

      {/* ═══ FOOTER ═══ */}
      <footer className="py-10" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <Logo size="sm" showTagline={false} />
          <p className="text-sm" style={{ color: C.textMut }}>&copy; {new Date().getFullYear()} EverBook. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 1 — HERO
   ══════════════════════════════════════════════════════ */
function SectionHero() {
  return (
    <section className="pt-28 pb-20 sm:pt-36 sm:pb-28 lg:pt-40 lg:pb-32" style={{ background: C.bg }} data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-sm font-medium tracking-[0.2em] uppercase mb-6"
              style={{ color: C.green }}
              data-testid="hero-eyebrow"
            >
              Your life. In motion.
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl lg:text-[4rem] font-light leading-[1.06] tracking-tight"
              style={{ color: C.text, fontFamily: serif }}
              data-testid="hero-headline"
            >
              This belongs in your{' '}
              <span className="italic" style={{ color: C.green }}>EverBook.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-5 text-xl sm:text-2xl lg:text-[1.625rem] italic"
              style={{ color: C.green, fontFamily: serif }}
              data-testid="hero-subheadline"
            >
              Not just saved—kept.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-6 text-base sm:text-lg lg:text-xl leading-[1.7] max-w-md"
              style={{ color: C.textSec }}
              data-testid="hero-support"
            >
              Photos, videos, voices, and moments&mdash;brought together
              into one living story.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-10 flex flex-col sm:flex-row items-start gap-4"
            >
              <div className="flex flex-col items-start">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full text-[15px] font-medium transition-all hover:scale-[1.03] hover:shadow-xl shadow-md whitespace-nowrap"
                  style={{ background: C.green, color: '#0A0A0A' }}
                  data-testid="hero-cta-primary"
                >
                  Start My Story — Free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <span className="mt-2.5 ml-1 text-xs tracking-wide" style={{ color: C.textMut }}>
                  Takes less than 60 seconds to begin
                </span>
              </div>
              <a
                href="#section-core"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-[15px] font-medium transition-all hover:bg-[#141414]/5 whitespace-nowrap"
                style={{ color: C.textSec }}
                data-testid="hero-cta-secondary"
              >
                <ChevronDown className="w-4 h-4" />
                See how it works
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-[480px]"
            data-testid="infinity-timeline-hero"
          >
            {/* Ambient radial glow behind the infinity — breathing, subtle */}
            <motion.div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.55, 0.85, 0.55] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(212,165,116,0.10) 0%, rgba(212,165,116,0.04) 40%, transparent 70%)',
                filter: 'blur(8px)',
              }}
            />
            <InfinityTimeline className="relative w-full h-full" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 1.5 — SCATTERED → TOGETHER (first scroll beat)
   ══════════════════════════════════════════════════════ */
function SectionScattered() {
  return (
    <section
      className="py-10 sm:py-14"
      style={{ background: C.bg }}
      data-testid="scattered-section"
    >
      <div className="max-w-3xl mx-auto px-6 text-center">
        <FadeIn>
          <div
            className="mx-auto w-12 h-px mb-6"
            style={{ background: `${C.green}55` }}
            aria-hidden
          />
        </FadeIn>

        <FadeIn delay={0.1}>
          <p
            className="text-xl sm:text-2xl lg:text-[1.75rem] font-light italic leading-snug"
            style={{ color: C.green, fontFamily: serif }}
            data-testid="scattered-transition"
          >
            EverBook brings it all together.
          </p>
        </FadeIn>

        <FadeIn delay={0.2}>
          <p
            className="mt-3 text-base sm:text-lg"
            style={{ color: C.textSec }}
            data-testid="scattered-closing"
          >
            Now everything has a place.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}



/* ══════════════════════════════════════════════════════
   SECTION 2 — CAMERA ROLL BREAK
   ══════════════════════════════════════════════════════ */
function SectionCameraRoll() {
  // Shared warm lifestyle photo set (also used as Before thumbs + After timeline thumbs)
  const WARM = [
    'https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=300&q=55&auto=format&fit=crop', // friends sunset
    'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=300&q=55&auto=format&fit=crop', // warm golden man
    'https://images.unsplash.com/photo-1519741497674-611481863552?w=300&q=55&auto=format&fit=crop', // couple golden hour
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=55&auto=format&fit=crop', // warm smiling man
  ];

  // Messy-camera-roll photos — real lifestyle images, scattered, rotated, overlapping.
  // Mix of locally-generated cinematic AI photos + verified warm Unsplash lifestyle shots.
  const beforeTiles = [
    { src: '/memories/sunday.jpg',  rot: -6, y: 0 },
    { src: WARM[0],                  rot: 3,  y: 8 },
    { src: '/memories/summer.jpg',  rot: -2, y: 18 },
    { src: WARM[1],                  rot: 5,  y: 4 },
    { src: '/memories/steps.jpg',   rot: -4, y: 22 },
    { src: WARM[2],                  rot: 2,  y: 10 },
    { src: '/memories/couple.jpg',  rot: -7, y: 26 },
    { src: WARM[3],                  rot: 4,  y: 2 },
    { src: '/memories/speech.jpg',  rot: -3, y: 14 },
    { src: WARM[0],                  rot: 6,  y: 20 },
  ];

  const afterItems = [
    { label: 'Summer 2025',     sub: '42 moments',           tone: 0.14, thumbs: [WARM[0]] },
    { label: 'Barcelona Trip',  sub: '118 photos · 6 videos', tone: 0.11, thumbs: [WARM[1], WARM[3]] },
    { label: 'Jordan\u2019s Wedding', sub: '3 chapters',     tone: 0.09, thumbs: [WARM[2]] },
    { label: 'First Marathon',  sub: '27 photos',            tone: 0.07, thumbs: [WARM[3]] },
  ];

  return (
    <section className="py-28 sm:py-36" style={{ background: C.bgAlt }} data-testid="camera-roll-section">
      <div className="max-w-6xl mx-auto px-6">
        <FadeIn>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-center"
            style={{ color: C.text, fontFamily: serif }}
          >
            Your life deserves more than{' '}
            <span className="italic">a camera roll.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.12}>
          <p className="mt-8 text-base sm:text-lg leading-[1.7] text-center max-w-xl mx-auto" style={{ color: C.textSec }}>
            Photos scatter. Moments fade. Names slip away.<br />
            EverBook weaves them into one living story—beautifully organized, instantly relivable.
          </p>
        </FadeIn>

        {/* Chaos → Clarity visual */}
        <FadeIn delay={0.25}>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            {/* BEFORE — messy, tilted, overlapping "photos" with ultra-faint real textures */}
            <div className="relative rounded-2xl p-6 overflow-hidden" style={{ background: '#1C1C1C', minHeight: 340 }}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ color: C.textMut }}>Before</p>
                <p className="text-[10px] tracking-wide" style={{ color: C.textMut }}>12,847 photos</p>
              </div>
              <div className="relative h-[240px]">
                {beforeTiles.map((t, i) => {
                  // Distribute 10 tiles across 4 columns × ~3 rows with soft overlap.
                  const col = i % 4;
                  const row = Math.floor(i / 4);
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.04 * i, duration: 0.5 }}
                      className="absolute w-[28%] aspect-square rounded-md overflow-hidden"
                      style={{
                        left: `${col * 24 + (row % 2 === 1 ? 6 : 0)}%`,
                        top: `${row * 64 + t.y * 0.35}px`,
                        transform: `rotate(${t.rot}deg)`,
                        boxShadow: '0 6px 14px rgba(212,165,116,0.10), 0 1px 2px rgba(212,165,116,0.08)',
                        background: '#0F0F0F',
                      }}
                    >
                      <img
                        src={t.src}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'saturate(0.72) contrast(0.92) brightness(0.97) blur(0.5px)' }}
                      />
                      {/* Soft neutral fade so tiles feel "fading" rather than fully lit */}
                      <div className="absolute inset-0 pointer-events-none" style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(30,25,18,0.18) 100%)',
                      }} />
                    </motion.div>
                  );
                })}
              </div>
              <p className="mt-4 text-xs italic" style={{ color: C.textMut }}>Scattered. Unnamed. Easy to lose.</p>
            </div>

            {/* AFTER — clean, structured, labelled timeline with circular memory thumbs */}
            <div className="relative rounded-2xl p-6" style={{ background: C.card, border: `1px solid ${C.border}`, minHeight: 340, boxShadow: '0 1px 2px rgba(212,165,116,0.04), 0 8px 24px rgba(212,165,116,0.06)' }}>
              <div className="flex items-center justify-between mb-5">
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ color: C.green }}>After — EverBook</p>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: C.green }} />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: C.green }} />
                  </span>
                  <span className="text-[10px] tracking-wide" style={{ color: C.green }}>Live</span>
                </div>
              </div>

              {/* Vertical timeline line */}
              <div className="relative pl-5">
                <div className="absolute left-[5px] top-1 bottom-1 w-px" style={{ background: `${C.green}30` }} />
                <div className="space-y-3.5">
                  {afterItems.map((it, i) => (
                    <motion.div
                      key={it.label}
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.12, duration: 0.6 }}
                      className="relative flex items-center gap-3"
                    >
                      <div className="absolute -left-5 w-2.5 h-2.5 rounded-full ring-4 ring-[#141414]" style={{ background: C.green }} />
                      <div
                        className="flex-1 rounded-xl pl-3 pr-4 py-2 flex items-center gap-3"
                        style={{ background: `rgba(212,165,116,${it.tone})` }}
                      >
                        {/* Real memory thumbs — small, circular, max 2 per row */}
                        <div className="flex -space-x-1.5 flex-shrink-0">
                          {it.thumbs.map((src, k) => (
                            <img
                              key={k}
                              src={src}
                              alt=""
                              aria-hidden
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-5 h-5 rounded-full object-cover ring-2 ring-white"
                              style={{ filter: 'saturate(0.95)' }}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium flex-1" style={{ color: C.text }}>{it.label}</span>
                        <span className="text-[11px]" style={{ color: C.textMut }}>{it.sub}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <p className="mt-5 text-xs font-medium flex items-center gap-1.5" style={{ color: C.green }}>
                <Sparkles className="w-3 h-3" />
                Your life—finally connected.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 3 — CORE SHIFT
   ══════════════════════════════════════════════════════ */
function SectionCoreShift() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const y3 = useTransform(scrollYProgress, [0, 1], [60, -60]);

  const pillars = [
    {
      Icon: Layers,
      title: 'Organized automatically',
      body: 'People, places, and chapters appear as your life unfolds—no tagging, no folders.',
      motion: y1,
      delay: 0.2,
    },
    {
      Icon: Clock,
      title: 'Instantly relivable',
      body: 'A cinematic timeline and life map bring any moment back in a tap.',
      motion: y2,
      delay: 0.32,
    },
    {
      Icon: Heart,
      title: 'Built for life',
      body: 'Private by default. Yours forever. Shared only when you choose.',
      motion: y3,
      delay: 0.44,
    },
  ];

  return (
    <section id="section-core" ref={ref} className="relative py-28 sm:py-36" style={{ background: C.bg }} data-testid="core-shift-section">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <FadeIn>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight"
            style={{ color: C.text, fontFamily: serif }}
          >
            From moments… to a <span className="italic">living story.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.12}>
          <p className="mt-8 text-base sm:text-lg leading-[1.7] max-w-xl mx-auto" style={{ color: C.textSec }}>
            Every photo, video, and memory—connected in a timeline that grows with you.
          </p>
        </FadeIn>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 max-w-4xl mx-auto">
          {pillars.map(({ Icon, title, body, motion: y, delay }) => (
            <FadeIn key={title} delay={delay}>
              <motion.div
                style={{ y }}
                className="text-left sm:text-center rounded-2xl p-7 h-full transition-all"
              >
                <div
                  className="w-12 h-12 rounded-full sm:mx-auto mb-5 flex items-center justify-center"
                  style={{ background: `${C.green}0E`, border: `1px solid ${C.green}1A` }}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: C.green }} />
                </div>
                <p className="text-lg font-medium mb-2" style={{ color: C.text, fontFamily: serif }}>{title}</p>
                <p className="text-sm leading-[1.65]" style={{ color: C.textSec }}>{body}</p>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 4 — EMOTIONAL
   ══════════════════════════════════════════════════════ */
function SectionEmotional() {
  // AI-generated memory photos — already cinematic, warm, with natural DOF.
  // Hosted locally at /memories/*.jpg to avoid external dependencies.
  const memories = [
    {
      src: '/memories/sunday.jpg',
      title: 'Sunday morning',
      sub: 'before everything changed',
      year: '2019',
    },
    {
      src: '/memories/steps.jpg',
      title: 'Her first steps',
      sub: 'I didn\u2019t know I\u2019d remember this forever',
      year: '2021',
    },
    {
      src: '/memories/speech.jpg',
      title: 'The speech',
      sub: 'I almost didn\u2019t record it',
      year: '2022',
    },
    {
      src: '/memories/summer.jpg',
      title: 'That summer',
      sub: 'we thought it would never end',
      year: '2023',
    },
    {
      src: '/memories/couple.jpg',
      title: 'Just us',
      sub: 'before life got loud',
      year: '2025',
    },
  ];

  // Lighter, brand-consistent tint — the AI images are already cinematic amber/brown.
  const unifiedTint = 'linear-gradient(160deg, rgba(120,75,40,0.18) 0%, rgba(80,50,25,0.14) 40%, rgba(40,25,14,0.32) 100%)';
  // Vertical focal gradient: top/center light, bottom darker for text.
  const focalGrad = 'linear-gradient(180deg, rgba(20,12,6,0.18) 0%, rgba(20,12,6,0.00) 25%, rgba(20,12,6,0.00) 55%, rgba(15,9,4,0.88) 100%)';

  return (
    <section className="relative py-28 sm:py-36 overflow-hidden" style={{ background: '#0F0905' }} data-testid="emotional-section">
      {/* subtle grain/glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 40%, rgba(255,240,200,0.08) 0%, transparent 60%)',
      }} />
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <FadeIn>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight tracking-tight"
            style={{ fontFamily: serif }}
          >
            Relive what <span className="italic">mattered.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.15}>
          <p className="mt-10 text-lg sm:text-xl leading-[1.7] text-white/70 max-w-lg mx-auto" style={{ fontFamily: serif }}>
            The laughter. The milestones.<br />
            The quiet moments you didn&apos;t know you&apos;d miss.
          </p>
        </FadeIn>

        {/* Memory cards — lightly softened real moments, unified warm amber cinematic tint */}
        <FadeIn delay={0.3}>
          <div className="mt-16 flex justify-center items-end gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
            {memories.map((m, i) => {
              const isCenter = i === 2;
              const isNear = i === 1 || i === 3;
              return (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 24, rotate: (i - 2) * 1.8 }}
                  whileInView={{ opacity: 1, y: 0, rotate: (i - 2) * 1.0 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, rotate: 0 }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="relative rounded-2xl overflow-hidden flex-shrink-0"
                  style={{
                    width: isCenter ? 180 : isNear ? 160 : 140,
                    height: isCenter ? 340 : isNear ? 300 : 270,
                    background: '#1A1008',
                    boxShadow: '0 18px 44px rgba(0,0,0,0.40), 0 2px 6px rgba(0,0,0,0.25)',
                  }}
                >
                  {/* AI photo — already has natural DOF; no artificial blur, tiny film softening only */}
                  <motion.img
                    src={m.src}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: 'saturate(0.92) contrast(0.96) brightness(0.96)', transform: 'scale(1.03)' }}
                    animate={{ scale: [1.03, 1.06, 1.03] }}
                    transition={{ duration: 16 + i * 0.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  {/* UNIFIED warm amber cinematic tint — identical on every card for coherence */}
                  <div className="absolute inset-0 pointer-events-none" style={{ background: unifiedTint }} />
                  {/* Focal clarity gradient — soft top, clearer middle, darker bottom for text */}
                  <div className="absolute inset-0 pointer-events-none" style={{ background: focalGrad }} />
                  {/* Subtle edge vignette — edges softer, emphasizes center */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: 'radial-gradient(ellipse 75% 65% at 50% 45%, transparent 0%, transparent 55%, rgba(30,18,10,0.35) 100%)',
                  }} />

                  {/* Caption — serif title + italic emotional tag + year */}
                  <div className="absolute inset-x-0 bottom-0 px-3.5 pt-6 pb-3.5 text-left">
                    <p className="text-white text-[13px] font-medium leading-tight" style={{ fontFamily: serif, textShadow: '0 1px 6px rgba(0,0,0,0.65)' }}>
                      {m.title}
                    </p>
                    <p className="text-white/85 text-[11px] italic leading-snug mt-0.5" style={{ fontFamily: serif, textShadow: '0 1px 6px rgba(0,0,0,0.65)' }}>
                      {m.sub}
                    </p>
                    <p className="text-white/55 text-[10px] mt-1.5 tracking-wide">{m.year}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </FadeIn>

        <FadeIn delay={0.6}>
          <p className="mt-12 text-[13px] tracking-[0.2em] uppercase text-white/40">
            Every moment · Every year · Every you
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 5 — IDENTITY ("EverBook moments")
   ══════════════════════════════════════════════════════ */
function SectionIdentity() {
  const tags = ['Family', 'Wins', 'Travel', 'Milestones', 'Everyday'];

  return (
    <section className="py-28 sm:py-36" style={{ background: C.bg }} data-testid="identity-section">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <FadeIn>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight"
            style={{ color: C.text, fontFamily: serif }}
          >
            These are your <span className="italic" style={{ color: C.green }}>EverBook moments.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.12}>
          <p className="mt-8 text-base sm:text-lg leading-[1.7] max-w-lg mx-auto" style={{ color: C.textSec }}>
            Not just photos. Not just videos.<br />
            The moments that define your life—captured, organized, and never lost.
          </p>
        </FadeIn>

        <FadeIn delay={0.25}>
          <div className="mt-14 flex flex-wrap justify-center gap-3">
            {tags.map((tag, i) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                className="px-5 py-2.5 rounded-full text-sm font-medium"
                style={{ background: `${C.green}0A`, color: C.green, border: `1px solid ${C.green}18` }}
              >
                {tag}
              </motion.span>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.4}>
          {/* Mini "EverBook moment" preview card — brings identity section alive */}
          <div className="mt-14 max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -4, boxShadow: '0 24px 60px rgba(212,165,116,0.14), 0 4px 12px rgba(212,165,116,0.08)' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="group relative rounded-2xl overflow-hidden text-left"
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                boxShadow: '0 12px 32px rgba(212,165,116,0.08), 0 2px 6px rgba(212,165,116,0.04)',
              }}
            >
              {/* Photo banner — real lifestyle image with warm brand overlay */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop"
                  alt="Sunday morning — warm light, her"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: 'saturate(0.95) contrast(0.98)' }}
                />
                {/* Warm brand gradient overlay (40% intensity) — preserves brand tone + guarantees text legibility */}
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(135deg, rgba(241,216,174,0.35) 0%, rgba(196,153,114,0.25) 45%, rgba(212,165,116,0.55) 100%)',
                }} />
                {/* Bottom gradient for text legibility */}
                <div className="absolute inset-x-0 bottom-0 h-24" style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
                }} />
                {/* Top badge */}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md"
                     style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)' }}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#141414]/95" />
                  <span className="text-[10px] font-semibold text-white tracking-wide">EverBook Moment</span>
                </div>
                {/* Overlay meta */}
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
                  <p className="text-white text-base font-medium leading-snug flex-1" style={{ fontFamily: serif, textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}>
                    Sunday mornings with her<br />
                    <span className="italic font-light text-white/80 text-sm">— before everything changed</span>
                  </p>
                  <p className="text-white/85 text-[10px] tracking-wide flex-shrink-0" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>2022</p>
                </div>
              </div>

              {/* Meta row */}
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {/* Stacked avatars */}
                  <div className="flex -space-x-2">
                    {['#C48B6A', '#C49560', '#E89968'].map((bg, i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full border-2 border-white"
                        style={{ background: `linear-gradient(135deg, ${bg}, ${bg}88)` }}
                      />
                    ))}
                  </div>
                  <span className="text-xs" style={{ color: C.textSec }}>2 people · coffee · quiet light</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" style={{ color: C.green }} />
                  <span className="text-[11px] font-medium" style={{ color: C.green }}>Kept forever</span>
                </div>
              </div>
            </motion.div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 6 — FUTURE
   ══════════════════════════════════════════════════════ */
function SectionFuture() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const opacity = useTransform(scrollYProgress, [0.2, 0.5], [0.4, 1]);
  const lineProgress = useTransform(scrollYProgress, [0.15, 0.65], [0, 1]);

  const years = [
    { y: '2020', count: 112 },
    { y: '2021', count: 247 },
    { y: '2022', count: 389 },
    { y: '2023', count: 502 },
    { y: '2024', count: 678 },
    { y: '2025', count: 834, now: true },
    { y: '2030', count: null, future: true },
    { y: '2040', count: null, future: true },
  ];

  return (
    <section ref={ref} className="relative py-28 sm:py-36" style={{ background: C.bgAlt }} data-testid="future-section">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <motion.div style={{ opacity }}>
          <FadeIn>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight"
              style={{ color: C.text, fontFamily: serif }}
            >
              Built for your <span className="italic">future self.</span>
            </h2>
          </FadeIn>

          <FadeIn delay={0.12}>
            <p className="mt-8 text-base sm:text-lg leading-[1.7] max-w-lg mx-auto" style={{ color: C.textSec }}>
              Years from now, this won&apos;t just be memories.<br />
              It will be your life—preserved, and ready to relive.
            </p>
          </FadeIn>

          {/* Dramatic timeline with growing line */}
          <FadeIn delay={0.25}>
            <div className="mt-16 sm:mt-20 relative max-w-3xl mx-auto">
              {/* Base line */}
              <div className="absolute left-0 right-0 top-[18px] h-px" style={{ background: `${C.textMut}30` }} />
              {/* Animated active line */}
              <motion.div
                className="absolute left-0 top-[18px] h-px origin-left"
                style={{ background: C.green, scaleX: lineProgress, width: '65%' }}
              />

              <div className="relative flex justify-between items-start gap-1">
                {years.map((yr, i) => (
                  <motion.div
                    key={yr.y}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.07, duration: 0.5 }}
                    className="flex flex-col items-center flex-1 min-w-0"
                  >
                    <div
                      className="rounded-full transition-all"
                      style={{
                        width: yr.now ? 14 : 8,
                        height: yr.now ? 14 : 8,
                        background: yr.future ? 'transparent' : (yr.now ? C.green : `${C.green}80`),
                        border: yr.future ? `1.5px dashed ${C.textMut}80` : 'none',
                        boxShadow: yr.now ? `0 0 0 6px ${C.green}20, 0 0 0 12px ${C.green}10` : 'none',
                      }}
                    />
                    <p
                      className="mt-4 font-medium whitespace-nowrap"
                      style={{
                        color: yr.now ? C.green : yr.future ? C.textMut : C.textSec,
                        fontWeight: yr.now ? 700 : 500,
                        fontFamily: yr.future ? serif : undefined,
                        fontStyle: yr.future ? 'italic' : undefined,
                        fontSize: yr.now ? 14 : 12,
                      }}
                    >
                      {yr.y}
                    </p>
                    {yr.count !== null && (
                      <p className="mt-1 text-[9px] sm:text-[10px] tracking-wide hidden sm:block whitespace-nowrap" style={{ color: yr.now ? C.green : C.textMut }}>
                        {yr.count} moments
                      </p>
                    )}
                    {yr.future && (
                      <p className="mt-1 text-[9px] sm:text-[10px] italic hidden sm:block" style={{ color: C.textMut }}>someday</p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Current year stat — visible on mobile only (simplified) */}
              <p className="mt-5 text-xs font-medium text-center sm:hidden" style={{ color: C.green }}>
                834 moments captured · still writing
              </p>

              <FadeIn delay={0.8}>
                <p className="mt-12 sm:mt-16 text-sm italic text-center" style={{ color: C.textSec, fontFamily: serif }}>
                  One day, you&apos;ll scroll back—and it will all be here.
                </p>
              </FadeIn>
            </div>
          </FadeIn>
        </motion.div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION 7 — FINAL CTA
   ══════════════════════════════════════════════════════ */
function SectionFinalCTA() {
  return (
    <section className="py-28 sm:py-36 relative overflow-hidden" style={{ background: C.bgAlt }} data-testid="final-cta-section">
      {/* warm radial glow centered */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(212,165,116,0.10) 0%, rgba(212,165,116,0.04) 40%, transparent 70%)',
      }} />
      <div className="max-w-3xl mx-auto px-6 text-center relative">
        <FadeIn>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight tracking-tight"
            style={{ fontFamily: serif }}
          >
            Start your life story <span className="italic">today.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.15}>
          <p className="mt-8 text-base sm:text-lg text-white/60 max-w-md mx-auto leading-[1.7]">
            Free to start. Yours forever.
          </p>
        </FadeIn>

        <FadeIn delay={0.3}>
          <div className="mt-10 flex flex-col items-center">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-[15px] font-medium transition-all hover:scale-[1.03] hover:shadow-2xl whitespace-nowrap"
              style={{ background: C.green, color: '#0A0A0A' }}
              data-testid="final-cta-btn"
            >
              Start My Story — Free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="mt-3 text-xs text-white/40">Takes less than 60 seconds</span>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
